import { useMemo } from 'react';
import { create } from 'zustand';
import type { InboxRangeParams } from '../services/ringcentral.service';

/**
 * The Communication Center's date-range preference (GP-1400).
 *
 * This used to be local state inside CommunicationCenter, which was fine while
 * the page was the only thing querying the inbox. The sidebar unread badge now
 * queries the same counts endpoint from the app shell, and a badge scoped to a
 * different window than the page would show a number the user can't find
 * anything behind. One store, read by both, keeps them in lockstep — including
 * live, while the user changes the range on the page.
 *
 * The localStorage keys are unchanged from the page-local implementation, so
 * everyone's saved preference survives the move (no migration).
 */

const LS_DAYS_FILTER = 'communication-days-filter';
const LS_CUSTOM_RANGE = 'communication-custom-range'; // JSON: { from, to }

/** Window the inbox falls back to when nothing is persisted (and the backend default). */
export const DEFAULT_INBOX_DAYS = 14;

export interface CustomRange {
  from: string; // YYYY-MM-DD
  to: string; // YYYY-MM-DD
}

interface CommunicationFilterState {
  /** Preset number of days, or 'custom' when an explicit From/To is in use (GP-539). */
  daysFilter: number | 'custom';
  customRange: CustomRange | null;
  setDaysFilter: (value: number | 'custom') => void;
  setCustomRange: (range: CustomRange) => void;
}

const readDaysFilter = (): number | 'custom' => {
  const saved = localStorage.getItem(LS_DAYS_FILTER);
  if (saved === 'custom') return 'custom';
  const parsed = saved ? parseInt(saved, 10) : NaN;
  return Number.isFinite(parsed) ? parsed : DEFAULT_INBOX_DAYS;
};

const readCustomRange = (): CustomRange | null => {
  const saved = localStorage.getItem(LS_CUSTOM_RANGE);
  if (!saved) return null;
  try {
    const parsed = JSON.parse(saved) as CustomRange;
    if (parsed?.from && parsed?.to) return parsed;
  } catch {
    // ignore malformed persisted value
  }
  return null;
};

export const useCommunicationFilterStore = create<CommunicationFilterState>((set) => ({
  daysFilter: readDaysFilter(),
  customRange: readCustomRange(),

  setDaysFilter: (value) => {
    localStorage.setItem(LS_DAYS_FILTER, String(value));
    set({ daysFilter: value });
  },

  setCustomRange: (range) => {
    localStorage.setItem(LS_CUSTOM_RANGE, JSON.stringify(range));
    set({ customRange: range });
  },
}));

/** Turn the stored preference into the range params the inbox endpoints take. */
export const toInboxRange = (
  daysFilter: number | 'custom',
  customRange: CustomRange | null,
): InboxRangeParams => {
  if (daysFilter === 'custom') {
    return customRange
      ? { from: customRange.from, to: customRange.to }
      : { days: DEFAULT_INBOX_DAYS };
  }
  return { days: daysFilter };
};

/**
 * The date window every inbox query — list, count chips, sidebar badge — runs
 * with. Memoised on the primitives so it's safe as a query-key input.
 */
export const useInboxRange = (): InboxRangeParams => {
  const daysFilter = useCommunicationFilterStore((state) => state.daysFilter);
  const customRange = useCommunicationFilterStore((state) => state.customRange);
  return useMemo(() => toInboxRange(daysFilter, customRange), [daysFilter, customRange]);
};
